import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deployToken } from "../../redux/slices/deployTokenSlice";
import { deployTokenStyles as styles } from "./deployToken.tailwind";
import PreviewGame from "./Sections/PreviewGame";
import BuyedTokens from "./Sections/SelectToken/BuyedTokens";
import routeConstant from "../../lib/constants/routeConstant";
import PlayerAndActionSection from "./PlayerAndActionSection";
import { getDeployedActionAndPlayers, getDeployedTokenDataById, updateCurrentTokenImages } from "./deployToken.data";
import { setMyAccountActiveTab } from "../../redux/slices/publicSlice";
import { MY_ACCOUNT_TABS } from "../MyAccount/myAccountTabsData";
import PoweredUpScreen from "./Sections/PoweredUpScreen";
import { POWER_UP_FLAG } from "../../lib/constants/globalConstant";
import CantPowerUpTokenModal from "../shared/Modals/CantPowerUpTokenModal/CantPowerUpTokenModal";
import SelectNextTokenModal from "../shared/Modals/SelectNextTokenSelectModal/SelectNextTokenModal";

const DeployToken = ({ matchId, refreshTokens, powerUp, selectedTokenId, setSelectedTokenId, setEditTokenId, editTokenId, currentStep, setCurrentStep, setMyAccountActiveTab, selectedTokenData }) => {
  const isEditingCurrentToken = editTokenId === selectedTokenId;
  const navigate = useNavigate();
  const canvasRef = useRef({});

  const { tokens: deployedTokens, purchasedTokensDetails } = useSelector((state) => state.deployToken); // get user-purchased tokens for this match
  const { user } = useSelector((state) => state.user);    // get user details
  const gameStatus = purchasedTokensDetails?.matchDetails?.powerUpFlag  


  const { currentSeason } = useSelector((state) => state.seasons);              // get current season details
  const actions = currentSeason?.actions;           // get current season actions
  const players = currentSeason?.homeTeam?.players; // get current season players


  const [isDeployed, setIsDeployed] = useState(false);  // to check is token deployed or not will tell based on selectedTokenId and return data
  const [tokenData, setTokenData] = useState([                      // tokenData to record selectedImages, tokenIndex, etc.
    {
      tokenIndex: 0,
      selectedImages: { actionImage: null, playerImage: null },
    },
  ]);
  const [deployedTokenImages, setDeployedTokenImages] = useState([  // tokenData to record selectedImages, tokenIndex, etc.
    { 
      tokenIndex: 0,
      selectedImages: { actionImage: null, playerImage: null },
    },
  ])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [nextTokenId, setNextTokenId] = useState(null);

  // to check is token deployed or not
  // if token is not deployed will return false
  // else it will return { isDeployed, deployedPlayerId, deployedActionName }
  const isTokenDeployed = (selectedTokenId) => getDeployedTokenDataById(selectedTokenId, deployedTokens, matchId, user?.userDetails?._id);

  const getTokenCardURI = (id) => {
    // Access the canvas using the ref passed from TokenDataContainer
    const canvas = canvasRef.current[id]; // canvasRef is a ref object that holds the canvas for each token

    if (!canvas) {
        console.error('Canvas not found!');
        return;
    }

    // Get the URI of the canvas content as a data URL (base64 encoded PNG)
    const uri = canvas.toDataURL('image/png');

    if(!uri) {
      console.error('uri not found!');
      return;
    }

    const byteString = atob(uri.split(',')[1]); // Decode base64
    const mimeString = uri.split(',')[0].split(':')[1].split(';')[0]; // Get MIME type

    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    
    for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
    }

    // Create a Blob from the array buffer
    const blob = new Blob([uint8Array], { type: mimeString });

    // Convert Blob to File
    const file = new File([blob], "cube-image.png", { type: mimeString });
    return file;
  };

  const powerUpToken = async() => {
    if(!deployedTokens || deployedTokens?.length === 0) return;

    const _tokensData = [...tokenData]; // fetch all tokensData (local state for selected-images)

    // get token based on selectedToken Index
    const _token = _tokensData?.find((token) => token.tokenIndex === selectedTokenId);

    if (_token?.selectedImages?.playerImage && _token?.selectedImages?.actionImage) {
      // if selected token has playerImage and actionImage
      const tokenId = deployedTokens[selectedTokenId]?._id;

      const file = getTokenCardURI(tokenId);

      if (!file) {
          console.error("File conversion failed.");
          return;
      }

      const dataToSend = {
        tokenId,
        seasonId: currentSeason?._id,
        matchId,
        playerId: _token.selectedImages.playerImage.id,
        actionName: _token.selectedImages.actionImage.text,
        actionId: _token.selectedImages.actionImage.id,
        file
      };

      const resultAction = await powerUp(dataToSend);
      setEditTokenId(null); // reset editing to false
      
      if (deployToken.fulfilled.match(resultAction)) {
        refreshTokens();
        // if the resultAction is fulfilled token will be deployed and deployedTokens will be updated
        setTokenData(_tokensData);
      }
    }
  }

  // it will handle next step(s)
  const handleNextStep = async () => {
    if(isEditingCurrentToken && currentStep === 1) {
      await powerUpToken();
      setCurrentStep(2);
      return;
    }else if(isEditingCurrentToken && currentStep === 2) {
      await powerUpToken();
      return;
    }

    if (currentStep === 1) {
      setCurrentStep(2); // If we are on step 1 we set the current step to 2
    } else if (currentStep === 2) {
      // If we are already on step 2
      await powerUpToken(); // Deploy Token
    }
  };

  const handleImageSelect = (image, key) => {
    setHasUnsavedChanges(true);
    const _isDeployed = isTokenDeployed(selectedTokenId);
    if (_isDeployed) {
      const _deployedTokenImage = deployedTokenImages?.find(
        (token) => token.tokenIndex === selectedTokenId
      );

      if (_deployedTokenImage?.selectedImages[key]?.src !== image?.src) {
        setEditTokenId(selectedTokenId);
      }else {
        setEditTokenId(null);
      }
    }

    const _tokens = [...tokenData];
    const _token = _tokens.find((token) => token.tokenIndex === selectedTokenId);

    updateCurrentTokenImages({
      tokenIndex: selectedTokenId,
      actionImage: key === "actionImage" ? image : _token?.selectedImages?.actionImage || null,
      playerImage: key === "playerImage" ? image : _token?.selectedImages?.playerImage || null,
      tokens: tokenData,
      setTokens: setTokenData
    });
  };

  const handleBackStep = () => {
    if (currentStep === 1) {
      // Redirect to the dashboard or previous page if no token is deployed
      navigate(routeConstant.MY_ACCOUNT_PAGE);
      setMyAccountActiveTab(MY_ACCOUNT_TABS.VIEW_GAMES)
    } else if (currentStep === 2) {
      // If we are on step 2, go back to step 1
      setCurrentStep(1); // Go back to the first step
    }
  };
  
  const callForIsDeployed = useCallback(() => {
    const deployed = isTokenDeployed(selectedTokenId);
    setIsDeployed(deployed);

    if(deployed && deployed?.isDeployed) {
      const { actionImage, playerImage } = getDeployedActionAndPlayers(actions, players, deployed);
      updateCurrentTokenImages({
        tokenIndex: selectedTokenId,
        actionImage,
        playerImage,
        tokens: tokenData,
        setTokens: setTokenData
      });

      updateCurrentTokenImages({
        tokenIndex: selectedTokenId,
        actionImage,
        playerImage,
        tokens: deployedTokenImages,
        setTokens: setDeployedTokenImages
      });
    }
  }, [selectedTokenId]);

  const handleNavigation = () => {
    navigate(routeConstant.MY_ACCOUNT_PAGE);
    setMyAccountActiveTab(MY_ACCOUNT_TABS.VIEW_GAMES)
  };
  
  useEffect(() => {
    callForIsDeployed()
  }, [callForIsDeployed]);

  // useEffect(() => {
  //   if (gameStatus === POWER_UP_FLAG.CANT_POWER_UP && matchId === purchasedTokensDetails?.matchDetails?.matchId) {
  //     navigate(routeConstant.MY_ACCOUNT_PAGE);
  //     setMyAccountActiveTab(MY_ACCOUNT_TABS.VIEW_GAMES);
  //   }
  // }, [gameStatus, matchId, purchasedTokensDetails?.matchDetails?.matchId]);

  return (
    <div className={styles.outerContainer}>
      {/* Top Heading */}
      <h1 className={styles.heading}>
        Power-up tokens to activate point multiplier
      </h1>

      <div className={styles.componentsContainer}>
        <div className={styles.colContainer}>
          {/* Court Preview */}
          <PreviewGame
            selectedTokenData={selectedTokenData}
            setSelectedTokenId={setSelectedTokenId}
            hasUnsavedChanges={hasUnsavedChanges}
            setShowUnsavedChangesModal={setShowUnsavedChangesModal}
            setNextTokenId={setNextTokenId}
          />

          {/* All User Owned Tokens */}
          <BuyedTokens
            tokenData={tokenData}
            selectedTokenId={selectedTokenId}
            setSelectedTokenId={setSelectedTokenId}
            isTokenDeployed={isTokenDeployed}
            editTokenId={editTokenId}
            setShowUnsavedChangesModal={setShowUnsavedChangesModal}
            setNextTokenId={setNextTokenId}
            hasUnsavedChanges={hasUnsavedChanges}
            canvasRef={canvasRef}
          />
        </div>

        {/* Container For Players and Action Images */}
        {gameStatus === POWER_UP_FLAG.NOT_POWERED_UP && 
          <PlayerAndActionSection
            currentStep={currentStep} // Step can be 1 or 2
            tokenData={tokenData}
            selectedTokenId={selectedTokenId}
            handleImageSelect={handleImageSelect}
            handleNextStep={handleNextStep}
            handleBackStep={handleBackStep}
            isDeployed={isDeployed}
            setSelectedTokenId={setSelectedTokenId}
            tokensLength={deployedTokens?.length}
            isEditingCurrentToken={isEditingCurrentToken}
          />
        }  

        {/* Powered Up Screen */}
        {gameStatus === POWER_UP_FLAG.POWERED_UP && 
          <PoweredUpScreen 
            tokenData={tokenData}
            selectedTokenId={selectedTokenId}
            setSelectedTokenId={setSelectedTokenId}
            purchasedTokens={deployedTokens}
            setMyAccountActiveTab={setMyAccountActiveTab}
            matchId={matchId}
            userId={user?.userDetails?._id}
          />
        }  

        {/* Powered Up Screen */}
        {gameStatus === POWER_UP_FLAG.CANT_POWER_UP && 
          <PoweredUpScreen 
            tokenData={tokenData}
            selectedTokenId={selectedTokenId}
            setSelectedTokenId={setSelectedTokenId}
            purchasedTokens={deployedTokens}
            setMyAccountActiveTab={setMyAccountActiveTab}
            matchId={matchId}
            userId={user?.userDetails?._id}
          />
        }    
      </div>
      
      {/* If tokens can't be powered up then this modal will be seen Screen */}  
      {gameStatus === POWER_UP_FLAG.CANT_POWER_UP && (
        <CantPowerUpTokenModal isOpen={true} onClose={handleNavigation} />
      )}
      {showUnsavedChangesModal && (
        <SelectNextTokenModal
          isOpen={true}
          setSelectedTokenId={setSelectedTokenId}
          nextTokenId={nextTokenId}
          setTokenData={setTokenData}
          setHasUnsavedChanges={setHasUnsavedChanges}
          onClose={() => setShowUnsavedChangesModal(false)}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    powerUp: (data) => dispatch(deployToken(data)),
    setMyAccountActiveTab: (data) => dispatch(setMyAccountActiveTab(data)),
  };
};
export default connect(null, mapDispatchToProps)(DeployToken);
